import React, { memo, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { mapOptionsListStyleToCSS } from '../../../../utils/styleMapper';
import { computeContainerStyle, computeCheckboxContainerStyle } from '../../../../utils/containerStyles';
import { Image } from '../../Image';
import { CheckBox } from '../../CheckBox';
import { OptionsItemText } from '../OptionsItemText';
import type { OptionsItemVariantProps } from './types';
import { getOptionItemA11yProps } from '../../../../utils/optionItemA11y';
import styles from '../OptionsItem.module.css';

/** Default container settings for BigImageTitle (backward compat) */
const IMAGE_DEFAULTS = { height: 75, vAlign: 'top' };
const TEXT_DEFAULTS = { height: 30, vAlign: 'bottom', contentAlign: 'top' };

export const BigImageTitle = memo(function BigImageTitle({
  item,
  index,
  locale,
  variables,
  selectable,
  selected,
  disabled,
  selectionMode,
  appearance,
  selectedAppearance,
  onSelect,
  className,
  style,
  'data-testid': testId,
  editPath,
  previewMode,
  cellHeightFromCellWidth,
}: OptionsItemVariantProps): React.ReactElement {
  const handleClick = useCallback(() => {
    if (selectable && !disabled && onSelect) {
      onSelect(item, index, !selected);
    }
  }, [selectable, disabled, onSelect, item, index, selected]);

  const activeStyle = selected ? selectedAppearance : appearance;
  const itemStyle: CSSProperties = {
    ...mapOptionsListStyleToCSS(activeStyle),
    ...(cellHeightFromCellWidth != null && { aspectRatio: `100 / ${cellHeightFromCellWidth}` }),
  };

  // Container settings from appearance (v2.11+), with variant-specific fallbacks
  const appRecord = appearance as Record<string, unknown> | undefined;
  const imgContainer = appRecord?.imageContainer as Record<string, unknown> | undefined;
  const txtContainer = appRecord?.textContainer as Record<string, unknown> | undefined;
  const cbxContainer = appRecord?.checkboxContainer as Record<string, unknown> | undefined;

  const imageContainerStyle = computeContainerStyle(imgContainer as Parameters<typeof computeContainerStyle>[0], IMAGE_DEFAULTS);
  const textContainerStyle: CSSProperties = {
    padding: 16,
    ...computeContainerStyle(txtContainer as Parameters<typeof computeContainerStyle>[0], TEXT_DEFAULTS),
    zIndex: 1,
  };
  const checkboxContainerStyle = computeCheckboxContainerStyle(cbxContainer as Parameters<typeof computeCheckboxContainerStyle>[0]);

  return (
    <div
      className={`${styles.card} ${styles.noPadding}${selected ? ` ${styles.selected}` : ''}${disabled ? ` ${styles.disabled}` : ''}${className ? ` ${className}` : ''}`}
      style={style ? { ...itemStyle, ...style } : itemStyle}
      onClick={selectable && !disabled ? handleClick : undefined}
      {...getOptionItemA11yProps({ selectable, selected, disabled, selectionMode, onActivate: handleClick })}
      data-testid={testId}
      data-edit-path={editPath}
    >
      <div style={imageContainerStyle}>
        <Image
          image={item.image}
          locale={locale}
          fillContainer
          previewMode={previewMode}
          editPath={editPath ? `${editPath}.image` : undefined}
        />
      </div>
      <div style={textContainerStyle}>
        <OptionsItemText label={item.title} locale={locale} variables={variables} editPath={editPath ? `${editPath}.title` : undefined} />
      </div>
      {checkboxContainerStyle && item.checkbox && (
        <div style={checkboxContainerStyle}>
          <CheckBox toggleMark={item.checkbox} />
        </div>
      )}
    </div>
  );
});

BigImageTitle.displayName = 'BigImageTitle';
